import {
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    InputLabel,
    Link,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import Loading from '../../components/Loading';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';
import {
    TUTORIAL_SECTIONS,
    pagesForSection,
} from '../../helpers/tutorials';

const emptyForm = {
    name: '',
    desktop_url: '',
    mobile_url: '',
    section: 'gerencial',
    page_keys: [],
};

function TutorialVideoFormPage({ isEdit }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checking, setChecking] = useState(!isMasterUser());
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});

    const availablePages = useMemo(() => pagesForSection(form.section), [form.section]);

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    if (!cancelled) {
                        setIsMaster(user.is_master);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsMaster(false);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setChecking(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isEdit || !id || checking || !isMaster) {
            return undefined;
        }
        setLoading(true);
        api.get(`tutorial-videos/${id}`)
            .then((response) => {
                const data = response?.data?.data || {};
                setForm({
                    name: data.name || '',
                    desktop_url: data.desktop_url || '',
                    mobile_url: data.mobile_url || '',
                    section: data.section || 'gerencial',
                    page_keys: Array.isArray(data.page_keys) ? data.page_keys : [],
                });
            })
            .catch(() => {
                Swal.fire('Erro!', 'Não foi possível carregar o vídeo.', 'error');
                navigate('/admin/tutorial-videos');
            })
            .finally(() => setLoading(false));
        return undefined;
    }, [checking, id, isEdit, isMaster, navigate]);

    const togglePage = (pageValue) => {
        setForm((prev) => {
            const exists = prev.page_keys.includes(pageValue);
            return {
                ...prev,
                page_keys: exists
                    ? prev.page_keys.filter((key) => key !== pageValue)
                    : [...prev.page_keys, pageValue],
            };
        });
    };

    const handleSectionChange = (section) => {
        const allowed = new Set(pagesForSection(section).map((page) => page.value));
        setForm((prev) => ({
            ...prev,
            section,
            page_keys: prev.page_keys.filter((key) => allowed.has(key)),
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const nextErrors = {};
        if (!form.name.trim()) {
            nextErrors.name = 'Informe o nome.';
        }
        if (!form.desktop_url.trim()) {
            nextErrors.desktop_url = 'Informe o link desktop.';
        }
        if (!form.section) {
            nextErrors.section = 'Selecione a seção.';
        }
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            return;
        }
        setSaving(true);
        setErrors({});
        const payload = {
            name: form.name.trim(),
            desktop_url: form.desktop_url.trim(),
            mobile_url: form.mobile_url.trim(),
            section: form.section,
            page_keys: form.page_keys,
        };
        const request = isEdit
            ? api.put(`tutorial-videos/${id}`, payload)
            : api.post('tutorial-videos', payload);
        request
            .then(() => {
                navigate('/admin/tutorial-videos');
            })
            .catch((error) => {
                const apiErrors = error?.response?.data?.errors;
                if (apiErrors) {
                    setErrors(apiErrors);
                } else {
                    Swal.fire(
                        'Erro!',
                        error?.response?.data?.message || 'Não foi possível salvar o vídeo.',
                        'error',
                    );
                }
            })
            .finally(() => setSaving(false));
    };

    if (checking || loading) {
        return <Loading />;
    }
    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Dashboard
                </Link>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin/tutorial-videos">
                    Vídeos tutoriais
                </Link>
                <Typography color="text.primary">{isEdit ? 'Editar' : 'Novo'}</Typography>
            </Breadcrumbs>

            <Typography variant="h4" fontWeight={700} gutterBottom>
                {isEdit ? 'Editar vídeo tutorial' : 'Novo vídeo tutorial'}
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, maxWidth: 720 }}>
                <TextField
                    fullWidth
                    required
                    label="Nome"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    sx={{ mb: 2 }}
                />
                <TextField
                    fullWidth
                    required
                    label="Link desktop (YouTube)"
                    value={form.desktop_url}
                    onChange={(e) => setForm((prev) => ({ ...prev, desktop_url: e.target.value }))}
                    error={Boolean(errors.desktop_url)}
                    helperText={errors.desktop_url || 'Usado por padrão em desktop e se o mobile estiver vazio.'}
                    sx={{ mb: 2 }}
                />
                <TextField
                    fullWidth
                    label="Link mobile (YouTube)"
                    value={form.mobile_url}
                    onChange={(e) => setForm((prev) => ({ ...prev, mobile_url: e.target.value }))}
                    error={Boolean(errors.mobile_url)}
                    helperText={errors.mobile_url || 'Opcional. Usado automaticamente em telas menores.'}
                    sx={{ mb: 2 }}
                />
                <FormControl fullWidth required error={Boolean(errors.section)} sx={{ mb: 2 }}>
                    <InputLabel id="section-label">Seção (tela Tutoriais)</InputLabel>
                    <Select
                        labelId="section-label"
                        label="Seção (tela Tutoriais)"
                        value={form.section}
                        onChange={(e) => handleSectionChange(e.target.value)}
                    >
                        {TUTORIAL_SECTIONS.map((section) => (
                            <MenuItem key={section.value} value={section.value}>
                                {section.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.section ? <FormHelperText>{errors.section}</FormHelperText> : null}
                </FormControl>

                <Typography variant="subtitle2" gutterBottom>
                    Exibir também nestas páginas
                </Typography>
                <FormHelperText sx={{ mt: 0, mb: 1 }}>
                    Se nenhuma for marcada, o vídeo aparece só em Tutoriais.
                </FormHelperText>
                <FormGroup sx={{ mb: 2 }}>
                    {availablePages.map((page) => (
                        <FormControlLabel
                            key={page.value}
                            control={(
                                <Checkbox
                                    checked={form.page_keys.includes(page.value)}
                                    onChange={() => togglePage(page.value)}
                                />
                            )}
                            label={page.label}
                        />
                    ))}
                </FormGroup>
                {errors.page_keys ? (
                    <FormHelperText error sx={{ mb: 2 }}>{errors.page_keys}</FormHelperText>
                ) : null}

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                    <Button
                        variant="outlined"
                        color="inherit"
                        disabled={saving}
                        onClick={() => navigate('/admin/tutorial-videos')}
                    >
                        Cancelar
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export function RegisterTutorialVideoPage() {
    return <TutorialVideoFormPage isEdit={false} />;
}

export function EditTutorialVideoPage() {
    return <TutorialVideoFormPage isEdit />;
}

export default TutorialVideoFormPage;
